import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, Trash2, Edit2, Moon, CalendarDays, X, Sun, Bell, Clock, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { remindersAPI, settingsAPI } from '../utils/api';
import { formatHijriDateDetailed, formatHijriFromStored, formatGregorianDateShort, getHijriTimeSince, getNextOccurrence, getRelativeTimeTranslated, gregorianToHijri, formatPersianFromStored, formatPersianDateShort, getPersianTimeSince } from '../utils/dateHelpers';
import BottomNav from '../components/BottomNav';

const ReminderDetailPage = () => {
  const { reminderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [reminder, setReminder] = useState(null);
  const [settings, setSettings] = useState({ hijriAdjustment: 0 });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pushSubscribed, setPushSubscribed] = useState(false);

  // Check browser push subscription status
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready
        .then(reg => reg.pushManager.getSubscription())
        .then(sub => setPushSubscribed(!!sub))
        .catch(() => setPushSubscribed(false));
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [rem, setts] = await Promise.all([
          remindersAPI.get(reminderId),
          settingsAPI.get()
        ]);
        setReminder(rem);
        setSettings(setts);
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reminderId]);

  const canEdit = reminder && (reminder.createdBy === user?.id || !reminder.createdBy || !reminder.groupId);

  const handleDelete = () => setShowDeleteModal(true);

  const confirmDelete = async () => {
    try {
      await remindersAPI.delete(reminderId);
      navigate('/home');
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleEdit = () => navigate(`/edit-reminder/${reminderId}`);

  const PROD_URL = 'https://app.iremindit.com';

  const getShareText = () => {
    if (!reminder) return '';
    const categoryLabel = t(`reminder.${reminder.category || 'event'}`);
    const titleLine = `${reminder.title} (${categoryLabel})`;
    const dateLine = displayDate;
    const descLine = reminder.description ? `${reminder.description}\n` : '';
    return `${titleLine}\n${dateLine}\n${descLine}\n${t('detail.powered')}\n${PROD_URL}`;
  };

  const copyShareText = () => {
    navigator.clipboard.writeText(getShareText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaWebShare = async () => {
    const categoryLabel = t(`reminder.${reminder.category || 'event'}`);
    if (navigator.share) {
      try { await navigator.share({ title: `${reminder.title} (${categoryLabel})`, text: getShareText() }); } catch { /* cancelled */ }
    } else {
      copyShareText();
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!reminder) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <p className="text-slate-500">{t('detail.not_found')}</p>
      </div>
    );
  }

  const isHijri = reminder.calendarType === 'hijri' || reminder.calendarType === 'both';
  const isPersian = reminder.calendarType === 'persian';

  // Calculate time since original event date
  const getTimeSince = () => {
    // For Persian reminders, use Jalali calendar difference
    if (isPersian && reminder.persianDate) {
      return getPersianTimeSince(reminder.persianDate);
    }

    // For Hijri reminders, use Hijri calendar difference
    if (isHijri && reminder.hijriDate) {
      return getHijriTimeSince(reminder.hijriDate, settings.hijriAdjustment || 0);
    }

    // Gregorian calculation
    const eventDate = new Date(reminder.date);
    eventDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (eventDate > now) return null;

    let years = now.getFullYear() - eventDate.getFullYear();
    let months = now.getMonth() - eventDate.getMonth();
    let days = now.getDate() - eventDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += prevMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    return { years, months, days };
  };

  // Next occurrence info for yearly reminders
  const getNextInfo = () => {
    if (!reminder.repeatYearly) return null;
    const adj = (isHijri) ? (settings.hijriAdjustment || 0) : 0;
    return getRelativeTimeTranslated(reminder.date, true, t, adj, reminder.hijriDate, reminder.calendarType, reminder.persianDate);
  };

  const timeSince = getTimeSince();
  const nextInfo = getNextInfo();

  // Determine card color theme
  const cardBg = isPersian ? 'bg-gradient-to-br from-orange-50 to-orange-100/50'
    : isHijri ? 'bg-gradient-to-br from-emerald-50 to-emerald-100/50'
    : 'bg-gradient-to-br from-blue-50 to-blue-100/50';
  const iconBg = isPersian ? 'bg-orange-500 text-white' : isHijri ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white';
  const calendarIcon = isPersian ? <Sun size={32} /> : isHijri ? <Moon size={32} /> : <CalendarDays size={32} />;
  const calendarLabel = isPersian ? t('reminder.persian') : isHijri ? t('reminder.hijri') : t('reminder.gregorian');
  const calendarBadge = isPersian ? 'bg-orange-100 text-orange-700' : isHijri ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';

  // Format displayed date
  const displayDate = isPersian && reminder.persianDate
    ? formatPersianFromStored(reminder.persianDate, lang)
    : isHijri && reminder.hijriDate
    ? formatHijriFromStored(reminder.hijriDate, lang)
    : isHijri
    ? formatHijriDateDetailed(new Date(reminder.date), lang)
    : formatGregorianDateShort(new Date(reminder.date), lang);

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => navigate('/home')} data-testid="back-button"
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
              <ArrowLeft size={20} className="text-slate-900" />
            </button>
            <div className="flex items-center gap-2">
              {canEdit && (
                <button onClick={handleEdit} data-testid="edit-reminder-button"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
                  <Edit2 size={18} className="text-slate-900" />
                </button>
              )}
              <button onClick={() => setShowShareModal(true)} data-testid="share-reminder-button"
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
                <Share2 size={18} className="text-slate-900" />
              </button>
              {canEdit && (
                <button onClick={handleDelete} data-testid="delete-reminder-detail-button"
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform text-red-600">
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Reminder Card */}
          <div className={`p-6 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-6 ${cardBg}`}>
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${iconBg}`}>
              {calendarIcon}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="reminder-title" style={{ fontFamily: 'Manrope, sans-serif' }}>{reminder.title}</h1>
            <p className="text-lg text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
              {displayDate}
            </p>
          </div>

          {/* Time Since / Time Until */}
          {(timeSince || nextInfo) && (
            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-6">
              {timeSince && (
                <div className="text-center" data-testid="time-since-section">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{t('detail.time_since')}</p>
                  <div className="flex items-center justify-center gap-3">
                    {timeSince.years > 0 && (
                      <div className="text-center">
                        <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{timeSince.years}</p>
                        <p className="text-xs text-slate-500">{t('detail.years')}</p>
                      </div>
                    )}
                    {(timeSince.years > 0 || timeSince.months > 0) && (
                      <>
                        {timeSince.years > 0 && <div className="text-2xl text-slate-300 font-light">:</div>}
                        <div className="text-center">
                          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{timeSince.months}</p>
                          <p className="text-xs text-slate-500">{t('detail.months')}</p>
                        </div>
                      </>
                    )}
                    <div className="text-2xl text-slate-300 font-light">:</div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{timeSince.days}</p>
                      <p className="text-xs text-slate-500">{t('detail.days')}</p>
                    </div>
                  </div>
                  {reminder.repeatYearly && nextInfo && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-sm text-slate-500">{t('detail.next_occurrence')}: <span className="font-semibold text-slate-700">{nextInfo}</span></p>
                    </div>
                  )}
                </div>
              )}
              {!timeSince && nextInfo && (
                <div className="text-center" data-testid="time-until-section">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('detail.time_until')}</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{nextInfo}</p>
                </div>
              )}
            </div>
          )}

          {/* Details */}
          <div className="space-y-4">
            {/* Group badge — shown prominently if this is a shared group reminder */}
            {reminder.groupId && (
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
                  <Users size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('detail.group_event')}</p>
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {reminder.createdBy === user?.id
                      ? t('detail.you')
                      : (reminder.createdByName || t('detail.group_member'))}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wider">{t('detail.category')}</h3>
              <p className="text-lg font-semibold text-slate-900 capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t(`reminder.${reminder.category}`) || reminder.category}
              </p>
            </div>
            {reminder.description && (
              <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wider">{t('detail.notes')}</h3>
                <p className="text-base text-slate-700 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{reminder.description}</p>
              </div>
            )}
            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider">{t('detail.settings')}</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t('detail.calendar_type')}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${calendarBadge}`}>
                    {calendarLabel}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t('detail.repeat')}</span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                    {reminder.repeatFrequency === 'once'
                      ? t('reminder.one_time')
                      : reminder.repeatFrequency === 'weekly'
                      ? t('reminder.weekly')
                      : reminder.repeatFrequency === 'monthly'
                      ? t('reminder.monthly')
                      : t('reminder.yearly')}
                  </span>
                </div>
                {reminder.groupId && reminder.createdBy && (
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="text-sm text-slate-600">{t('detail.created_by')}</span>
                    <span className="text-sm font-semibold text-slate-900">
                      {reminder.createdBy === user?.id
                        ? t('detail.you')
                        : (reminder.createdByName || t('detail.group_member'))}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Notification Details */}
            <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider">{t('detail.remind_before')}</h3>

              {/* When chips */}
              <div className="flex flex-wrap gap-2 mb-4">
                {(Array.isArray(reminder.remindBeforeDays) ? reminder.remindBeforeDays : [reminder.remindBeforeDays ?? 0]).map(days => (
                  <span key={days} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                    {Number(days) === 0 ? t('reminder.same_day') : `${days} ${t('detail.days_before')}`}
                  </span>
                ))}
              </div>

              {/* Time */}
              <div className="flex items-center gap-2 mb-3 border-t border-slate-100 pt-3">
                <Clock size={14} className="text-slate-400" />
                <span className="text-sm text-slate-500">{t('reminder.reminder_time')}:</span>
                <span className="text-sm font-semibold text-slate-800">{(() => {
                  let h24, m;
                  if (reminder.use_custom_time && reminder.reminder_hour != null) {
                    h24 = Number(reminder.reminder_hour);
                    m = Number(reminder.reminder_minute || 0);
                  } else {
                    h24 = settings.default_reminder_hour != null ? Number(settings.default_reminder_hour) : 9;
                    m = settings.default_reminder_hour != null ? Number(settings.default_reminder_minute || 0) : 0;
                  }
                  const per = h24 < 12 ? 'AM' : 'PM';
                  const h = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
                  return `${h}:${String(m).padStart(2, '0')} ${per}`;
                })()}</span>
              </div>

              {/* Channels */}
              <div className="flex items-center gap-2 flex-wrap">
                <Bell size={14} className="text-slate-400" />
                <span className="text-sm text-slate-500">{t('detail.channels')}:</span>
                {settings.notificationsEnabled !== false && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                    {t('detail.channel_email')}
                  </span>
                )}
                {pushSubscribed && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
                    {t('detail.channel_app')}
                  </span>
                )}
                {settings.notificationsEnabled === false && !pushSubscribed && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-400">
                    {t('detail.channel_none')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center" style={{ zIndex: 10000 }}>
          <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white w-full max-w-md rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('detail.share')}</h2>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20} /></button>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl mb-4 whitespace-pre-wrap text-sm text-slate-700 font-mono">{getShareText()}</div>
            <div className="flex gap-3">
              <button onClick={copyShareText} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold">{copied ? t('detail.copied') : t('detail.copy')}</button>
              <button onClick={shareViaWebShare} className="flex-1 bg-slate-900 text-white py-3 rounded-full font-semibold">{t('detail.share_btn')}</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6" style={{ zIndex: 10000 }}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-sm rounded-3xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 size={32} className="text-red-600" /></div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('detail.delete_title')}</h2>
              <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>{reminder.title} {t('detail.delete_desc')}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold active:scale-95 transition-transform">{t('common.cancel')}</button>
              <button onClick={confirmDelete} data-testid="confirm-delete-detail-button" className="flex-1 bg-red-600 text-white py-3 rounded-full font-semibold active:scale-95 transition-transform">{t('common.delete')}</button>
            </div>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ReminderDetailPage;
